import { useState } from 'react'
import { CookieBar } from '../../components/CookieBar'
import { Footer } from '../../components/Footer'
import { Header } from '../../components/Header'
import { CommonPageDataFragment } from '../../data/CommonPageDataFragment'
import { GenericPageLocaleFragment } from '../../data/GenericPageLocaleFragment'
import { combineUrl } from '../../data/combineUrl'
import { contember } from '../../utilities/contember'
import { getMediaItems } from '../../utilities/getMediaItem'
import type { InferDataLoaderProps } from '../../utilities/handlers'
import { handleGetStaticPaths, handleGetStaticProps } from '../../utilities/handlers'
import { createSlug, getMediaCategory } from '../../utilities/getMediaCategory'
import { MediaMapList } from '../../components/MediaMapList'
import { PageTitle } from '../../components/PageTitle'
import { Breadcrumbs } from '../../components/Breadcrumbs'
import { Container } from '../../components/Container'
import s from './[path].module.sass'
import fetch from 'node-fetch'
import Head from 'next/head'

export type MediaMapPageProps = InferDataLoaderProps<typeof getStaticProps>

export default function MediaMapsPage(props: MediaMapPageProps & { popis: string }) {
	const commonData = props.commonData

	const [isCookieBarOpen, setIsCookieBarOpen] = useState(false)

	return (
		<>
			{commonData.getHeaderLocale && (
				<Header
					header={commonData.getHeaderLocale}
					newsletterModal={commonData.getNewsletterFormLocale}
					parentPages={props.parentPages}
					resultPageUrl={props.commonData.getResultPage?.localesByLocale?.link?.url}
				/>
			)}
			<div className={s.BreadcrumbNav}>
				<div>
					{props.menuItems && props.menuItems.length >= 2 && (
						<Breadcrumbs
							breadcrumb={[
								{
									url: props.mediaMapGenericPage.getGenericPageLocale?.link?.url ?? '',
									title: props.mediaMapGenericPage.getGenericPageLocale?.title,
								},
								{
									url: props.category.slug,
									title: props.category.name,
								},
							]}
						/>
					)}
				</div>
			</div>
			<PageTitle title={props.category.name} />
			<Head>
				<title>{props.category.name}</title>
			</Head>
			<div className={s.MenuCategories}>
				{props.menuItems.map((menuItem) => (
					<a key={menuItem.title} href={menuItem.link?.url} className={s.MenuCategoriesStyle}>
						{menuItem.title}
					</a>
				))}

				{props.mediaCategoryNames.flatMap((categoryName) => (
					<a key={categoryName.slug} href={`/category/${categoryName.slug}`} className={s.MenuCategory}>
						{categoryName.name}
					</a>
				))}
			</div>
			{props.popis && (
				<Container size="wide">
					<p style={{ textAlign: 'center', maxWidth: '800px', margin: '0 auto', padding: '20px 0', lineHeight: '1.6' }}>
						{props.popis}
					</p>
				</Container>
			)}
			<Container size="wide">
				<MediaMapList items={props.mapCategoryItems} />
			</Container>
			{commonData.getCookieBarLocale && (
				<CookieBar
					cookieBar={commonData.getCookieBarLocale}
					isCookieBarOpen={isCookieBarOpen}
					setIsCookieBarOpen={setIsCookieBarOpen}
				/>
			)}
			{commonData.getFooterLocale && (
				<Footer
					footer={commonData.getFooterLocale}
					newsletterModal={commonData.getNewsletterFormLocale}
					openCookieBar={() => setIsCookieBarOpen(true)}
				/>
			)}
		</>
	)
}

export const getStaticPaths = handleGetStaticPaths(async () => {
	const mediaCategoryPosts = await getMediaCategory()

	const paths = mediaCategoryPosts.map((post) => {
		return {
			params: {
				path: post.slug,
			},
		}
	})
	return {
		paths: paths,
		fallback: 'blocking',
	}
})

export const getStaticProps = handleGetStaticProps(async (context) => {
	const { locale } = combineUrl(context)

	if (typeof locale !== 'string') {
		throw new Error('Invalid locale')
	}

	const [commonData, mediaCategories] = await Promise.all([
		contember.query(CommonPageDataFragment(locale)),
		getMediaCategory(),
	])

	if (!context.params?.path) {
		return { notFound: true, revalidate: 300 }
	}

	const slug = context.params.path

	if (mediaCategories.length === 0) {
		return { notFound: true, revalidate: 300 }
	}

	const category = mediaCategories.find((cat) => {
		const categorySlug = createSlug(cat.slug)
		return categorySlug === slug
	})

	if (!category) {
		return { notFound: true, revalidate: 300 }
	}

	interface PopisCategory {
		kategorie: string
		popis: string
	}

	const [allItems, mediaMapGenericPage, popisCategories] = await Promise.all([
		getMediaItems(),
		contember.query({
			getGenericPageLocale: [{ by: { link: { url: '/mapa-medii' } } }, GenericPageLocaleFragment('cs')],
			listGenericPageLocale: [
				{ filter: { root: { parentPage: { locales: { link: { url: { eq: '/mapa-medii' } } } } } } },
				{ title: true, link: [{}, { url: true }] },
			],
		}),
		fetch('http://rating.nfnz.cz.s3.amazonaws.com/mapa_medii_json/mapa_medii_popis.json')
			.then((res) => res.json() as Promise<PopisCategory[]>)
			.catch(() => [] as PopisCategory[]),
	])

	const mapCategoryItems = allItems.filter((item) => createSlug(item.detail_o_webu.kategorie[0]) === slug)

	const matchingCategory = popisCategories.find((cat) => cat.kategorie === category.name)
	const popis = matchingCategory?.popis || ''

	return {
		props: {
			commonData,
			category,
			mapCategoryItems,
			mediaMapGenericPage,
			menuItems: mediaMapGenericPage.listGenericPageLocale,
			mediaCategoryNames: mediaCategories,
			parentPages: commonData.getMediaMapPage?.page?.localesByLocale?.link?.url
				? [
						{
							url: commonData.getMediaMapPage?.page?.localesByLocale?.link?.url,
							title: commonData.getMediaMapPage?.page?.localesByLocale.title,
						},
				  ]
				: [],
			popis,
		},
		revalidate: 300,
	}
})
